const React = require('react')
const { useHistory } = require('react-router-dom')
const { UncontrolledTooltip, Table } = require('reactstrap')
const { useNetworkConfig } = require('@contexts')
const { LoadingSpinner, CopyButton } = require('@components')
const { useFormatBytes, useResponsive, BREAKPOINTS } = require('@hooks')

require('@styles/pages/transaction/internal.transaction.css')

const TransactionInternal = props => {
    const { loading, data } = props
    const history = useHistory()
    const { network } = useNetworkConfig()
    const formatBytes = useFormatBytes()
    const isMobile = useResponsive(BREAKPOINTS.large)

    return (
        <div className='box table-section'>
            {loading ? (
                <div className='d-flex justify-content-center align-items-center' style={{ height: '300px' }}>
                    <LoadingSpinner />
                </div>
            ) : (
                <>
                    <span
                        style={{
                            marginBottom: '16px',
                            display: 'block',
                            color: 'var(--white-color)',
                            wordWrap: 'break-word',
                            whiteSpace: 'normal'
                        }}
                    >
                        The contract call&nbsp;
                        <span style={{ fontWeight: 'bold' }}>From</span>
                        &nbsp;
                        {data?.details.from.type === 'SCA' && (
                            <>
                                <i
                                    className='fas fa-file-alt'
                                    style={{
                                        fontSize: '14px',
                                        color: 'var(--grey-color)'
                                    }}
                                />
                                &nbsp;
                            </>
                        )}
                        <span
                            id='transaction-internal-from'
                            className={`${network.name}-text-color cursor-pointer`}
                            onClick={() => history.push(`/${network.name}/address/${data?.details.from.address}`)}
                            style={{ display: 'inline-block' }}
                        >
                            {data && formatBytes(data?.details.from.address, 30, 10, 9)}
                        </span>
                        &nbsp;
                        <span style={{ fontWeight: 'bold' }}>To</span>
                        &nbsp;
                        {data?.details.to.type === 'SCA' && (
                            <>
                                <i
                                    className='fas fa-file-alt'
                                    style={{
                                        fontSize: '14px',
                                        color: 'var(--grey-color)'
                                    }}
                                />
                                &nbsp;
                            </>
                        )}
                        <span
                            id='transaction-internal-to'
                            className={`${network.name}-text-color cursor-pointer`}
                            onClick={() => history.push(`/${network.name}/address/${data?.details.to.address}`)}
                            style={{ display: 'inline-block' }}
                        >
                            {data && formatBytes(data?.details.to.address, 30, 10, 9)}
                        </span>
                        &nbsp;produced {data?.traces.length} Internal Transactions
                        <UncontrolledTooltip placement='top' target='transaction-internal-from'>
                            {data?.details.from.address}
                        </UncontrolledTooltip>
                        <UncontrolledTooltip placement='top' target='transaction-internal-to'>
                            {data?.details.to.address}
                        </UncontrolledTooltip>
                    </span>
                    <Table
                        responsive
                        className='transaction-sub-table mb-0'
                        style={isMobile ? { display: 'block', overflowX: 'auto', whiteSpace: 'nowrap' } : {}}
                    >
                        <thead>
                            <tr>
                                <th>Type Trace Address</th>
                                <th>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}
                                    >
                                        <span>Method</span>
                                        <div
                                            id='method-tooltip'
                                            style={{
                                                width: '20px',
                                                height: '20px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <i
                                                className='fas fa-circle-question'
                                                style={{
                                                    fontSize: '14px',
                                                    color: 'var(--grey-color)'
                                                }}
                                            />
                                        </div>
                                        <UncontrolledTooltip placement='top' target='method-tooltip'>
                                            Function executed based on decoded input data. For unidentified functions,
                                            method ID is displayed instead
                                        </UncontrolledTooltip>
                                    </div>
                                </th>
                                <th>From</th>
                                <th>To</th>
                                <th style={{ textAlign: 'right' }}>Value</th>
                                <th style={{ textAlign: 'right' }}>Gas Limit</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data?.traces.map((trace, index) => (
                                <tr key={index}>
                                    <td>
                                        <div
                                            id={`transaction-trace-status-${index}`}
                                            className={`status-box ${trace.error ? 'fail' : 'success'}`}
                                            style={{ backgroundColor: 'transparent' }}
                                        >
                                            <i
                                                className={`fas ${
                                                    trace.error ? 'fa-times-circle' : 'fa-circle-check'
                                                } status-icon`}
                                            />
                                        </div>
                                        {trace.error && (
                                            <UncontrolledTooltip
                                                placement='top'
                                                target={`transaction-trace-status-${index}`}
                                            >
                                                {`Error - [${trace.error}] error`}
                                            </UncontrolledTooltip>
                                        )}
                                        &nbsp;
                                        {trace.traceType}
                                        {trace.traceAddress?.map(subtrace => (
                                            <>_{subtrace}</>
                                        ))}
                                    </td>
                                    <td>
                                        <span id={`method-id-${index}`} className='method-id'>
                                            {trace.functionName}
                                        </span>
                                        <UncontrolledTooltip placement='top' target={`method-id-${index}`}>
                                            {trace.functionName}
                                        </UncontrolledTooltip>
                                    </td>
                                    <td>
                                        {trace.from.type === 'SCA' && (
                                            <>
                                                <i
                                                    className='fas fa-file-alt'
                                                    style={{
                                                        fontSize: '14px',
                                                        color: 'var(--grey-color)'
                                                    }}
                                                />
                                                &nbsp;
                                            </>
                                        )}
                                        <span
                                            id={`transaction-trace-from-${index}`}
                                            className={`${network.name}-text-color cursor-pointer`}
                                            onClick={() =>
                                                history.push(`/${network.name}/address/${trace.from.address}`)
                                            }
                                        >
                                            {formatBytes(trace.from.address, 30, 10, 9)}
                                        </span>
                                        &nbsp;
                                        <CopyButton
                                            id={`copy-transaction-trace-from-${index}`}
                                            data={trace.from.address}
                                            text={'Copy address'}
                                        />
                                        <UncontrolledTooltip placement='top' target={`transaction-trace-from-${index}`}>
                                            {trace.from.address}
                                        </UncontrolledTooltip>
                                    </td>
                                    <td>
                                        {trace.to.type === 'SCA' && (
                                            <>
                                                <i
                                                    className='fas fa-file-alt'
                                                    style={{
                                                        fontSize: '14px',
                                                        color: 'var(--grey-color)'
                                                    }}
                                                />
                                                &nbsp;
                                            </>
                                        )}
                                        <span
                                            id={`transaction-trace-to-${index}`}
                                            className={`${network.name}-text-color cursor-pointer`}
                                            onClick={() => history.push(`/${network.name}/address/${trace.to.address}`)}
                                        >
                                            {formatBytes(trace.to.address, 30, 10, 9)}
                                        </span>
                                        &nbsp;
                                        <CopyButton
                                            id={`copy-transaction-trace-to-${index}`}
                                            data={trace.to.address}
                                            text={'Copy address'}
                                        />
                                        <UncontrolledTooltip placement='top' target={`transaction-trace-to-${index}`}>
                                            {trace.to.address}
                                        </UncontrolledTooltip>
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                        {(trace.value / 10 ** 18).toLocaleString(undefined, {
                                            minimumFractionDigits: 0,
                                            maximumFractionDigits: 18
                                        })}
                                        &nbsp;
                                        {network.data?.token}
                                    </td>
                                    <td style={{ textAlign: 'right' }}>{trace.gas.toLocaleString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </>
            )}
        </div>
    )
}

module.exports = { TransactionInternal }
